import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { v4 as uuid } from 'uuid';
import { getTipoModeloLabelByValue } from '../../shared-components/select-tipo-modelo/QCXFinalSelectTipoModeloAutocomplete';
import { unnormalizeNumeral } from '../../utils/general/general-utils';
import { parseToNumber } from '../../utils/general/parse-utils';
import { isSubCreateMode } from '../../utils/store/store-utils';
import {
  BACKGROUND_CREATE_MODE,
  CONSULT_MODE,
  CREATE_MODE,
  NONE_BACKGROUND_MODE,
  NONE_MODE,
  NONE_SUB_MODE,
  SUB_CONSULT_MODE,
  SUB_CREATE_MODE,
  SUB_UPDATE_MODE,
  UPDATE_MODE,
} from '../mode';
import {
  ALTERNATIVE_LOADING_STATUS,
  FAILURE_STATUS,
  IDLE_STATUS,
  LOADING_STATUS,
  SUCCESS_STATUS,
} from '../status';
import {
  fetchAll,
  fetchById,
  activateById,
  inactivateById,
} from './tabelaArmazenagemAPI';
import i18n from '../../i18n';

const initialState = {
  status: IDLE_STATUS,
  mode: {
    main: NONE_MODE,
    background: NONE_BACKGROUND_MODE,
    sub: NONE_SUB_MODE,
  },
  error: null,
  response: {
    status: null,
    message: null,
    data: null,
  },
  list: [],
  model: {
    id: null,
    insertionDate: null,
    code: null,
    description: null,
    active: true,
    gris: null,
    icms: null,
    valorAdicionalCarretaLs: null,
    viaTransporte: {
      id: null,
    },
    tipoEquipamento: null,
    tipoContainer: {
      id: null,
      insertionDate: null,
      code: null,
      description: null,
      active: null,
    },
    moeda: {
      id: null,
    },
    redestinacao: null,
    calculo: null,
    periodos: [],
    modelosLancamento: [],
    clientes: [],
  },
  related: {
    model: {
      tabelaPeriodo: {},
      modeloLancamento: {},
    },
  },
  locked: {
    model: {
      modeloLancamento: {},
    },
  },
};

const fetchAllAsync = createAsyncThunk(
  'tabelaArmazenagem/fetchAll',
  async () => {
    const { data } = await fetchAll();
    return { data };
  }
);

const fetchByIdAsync = createAsyncThunk(
  'tabelaArmazenagem/fetchById',
  async (data) => {
    const response = await fetchById(data);
    return {
      response: {
        status: response.status,
        data: response.data,
      },
    };
  }
);

const activateByIdAsync = createAsyncThunk(
  'tabelaArmazenagem/activateById',
  async (id) => {
    const { status, data } = await activateById(id);
    return { response: { status, data } };
  }
);

const inactivateByIdAsync = createAsyncThunk(
  'tabelaArmazenagem/inactivateById',
  async (id) => {
    const { status, data } = await inactivateById(id);
    return { response: { status, data } };
  }
);

const tabelaArmazenagemSlice = createSlice({
  name: 'tabelaArmazenagem',
  initialState,
  reducers: {
    changeStatusTo: (state, action) => {
      state.status = action.payload.status;
    },
    loading: (state) => {
      state.status = LOADING_STATUS;
    },
    success: (state) => {
      state.status = SUCCESS_STATUS;
    },
    failure: (state) => {
      state.status = FAILURE_STATUS;
    },
    resetStatus: (state) => {
      state.status = IDLE_STATUS;
    },
    changeToCreateMode: (state) => {
      state.mode.main = CREATE_MODE;
    },
    changeToConsultMode: (state) => {
      state.mode.main = CONSULT_MODE;
    },
    changeToUpdateMode: (state) => {
      state.mode.main = UPDATE_MODE;
    },
    resetMode: (state) => {
      state.mode.main = NONE_MODE;
    },
    changeToBackgroundCreateMode: (state) => {
      state.mode.background = BACKGROUND_CREATE_MODE;
    },
    resetBackgroundMode: (state) => {
      state.mode.background = NONE_BACKGROUND_MODE;
    },
    changeToSubCreateMode: (state) => {
      state.mode.sub = SUB_CREATE_MODE;
    },
    changeToSubConsultMode: (state) => {
      state.mode.sub = SUB_CONSULT_MODE;
    },
    changeToSubUpdateMode: (state) => {
      state.mode.sub = SUB_UPDATE_MODE;
    },
    resetSubMode: (state) => {
      state.mode.sub = NONE_SUB_MODE;
    },
    setModel: (state, action) => {
      const updatedPeriodosList = action.payload.periodos.map(
        (current) => ((current?.id === null)
          ? {
            ...current,
            id: uuid(),
          }
          : current
        )
      );

      const updatedModelosLancamentoList = action.payload.modelosLancamento.map(
        (current) => ((current?.id === null)
          ? {
            ...current,
            id: uuid(),
          }
          : current
        )
      );

      state.model = {
        ...action.payload,
        gris: `${action.payload?.gris}`,
        icms: `${action.payload?.icms}`,
        valorAdicionalCarretaLs: unnormalizeNumeral(action.payload?.valorAdicionalCarretaLs),
        periodos: updatedPeriodosList,
        modelosLancamento: updatedModelosLancamentoList,
      };
    },
    addToList: (state, action) => {
      state.list = [...state.list, action.payload.data];
    },
    updateOnList: (state, action) => {
      state.list = state.list.map((current) => {
        if (current.id === action.payload.data?.id) {
          return action.payload.data;
        }
        return current;
      });
    },
    resetList: (state) => {
      state.list = [];
    },
    setRelatedTabelaPeriodoModel: (state, action) => {
      state.related.model.tabelaPeriodo = {
        ...action.payload,
        porcentagem: `${action.payload?.porcentagem}`,
      };
      state.mode.sub = SUB_CONSULT_MODE;
    },
    addPeriodo: (state, action) => {
      const lastAdded = action.payload.data;
      const isPeriodoExistente = (
        state.model.periodos.some(
          (periodo) => parseToNumber(periodo?.periodo) === parseToNumber(lastAdded?.periodo)
        )
      );

      if (isPeriodoExistente) {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroAdicionarTabelaPeriodo', { periodo: lastAdded?.periodo }),
        };
        return;
      }

      const updatedTabelaPeriodos = [
        ...state.model.periodos,
        {
          ...action.payload.data,
          id: uuid(),
        }
      ];

      state.model.periodos = updatedTabelaPeriodos.sort((a, b) => {
        const firstPeriodo = parseToNumber(a?.periodo);
        const secondPeriodo = parseToNumber(b?.periodo);
        return firstPeriodo - secondPeriodo;
      });

      state.related.model.tabelaPeriodo = {};
      state.mode.sub = NONE_SUB_MODE;
    },
    updatePeriodo: (state, action) => {
      const updated = action.payload.data;
      const filteredTabelaPeriodosList = state.model.periodos.filter(
        (periodo) => periodo?.id !== updated?.id
      );

      const isPeriodoExistente = filteredTabelaPeriodosList.some(
        (periodo) => parseToNumber(periodo?.periodo) === parseToNumber(updated?.periodo)
      );

      if (isPeriodoExistente) {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroAdicionarTabelaPeriodo', { periodo: updated?.periodo }),
        };
        return;
      }

      const updatedTabelaPeriodosList = state.model.periodos.map(
        (current) => ((current?.id === updated?.id)
          ? updated : current
        )
      );

      state.model.periodos = updatedTabelaPeriodosList.sort((a, b) => {
        const firstPeriodo = parseToNumber(a?.periodo);
        const secondPeriodo = parseToNumber(b?.periodo);
        return firstPeriodo - secondPeriodo;
      });
      state.mode.sub = SUB_CONSULT_MODE;
    },
    removePeriodoById: (state, action) => {
      const { id } = action.payload.data;
      const updatedTabelaPeriodosList = state.model.periodos.filter((current) => (
        current?.id !== id
      ));

      state.model.periodos = updatedTabelaPeriodosList;
      state.related.model.tabelaPeriodo = initialState.related.model.tabelaPeriodo;
      state.mode.sub = NONE_SUB_MODE;
    },
    resetRelatedPeriodoModel: (state) => {
      state.related.model.tabelaPeriodo = {};

      if (!isSubCreateMode(state.mode.sub)) {
        state.mode.sub = NONE_SUB_MODE;
      }
    },
    setRelatedModeloLancamentoModel: (state, action) => {
      state.related.model.modeloLancamento = {
        ...action.payload,
        valor: unnormalizeNumeral(action.payload?.valor),
      };
      state.mode.sub = SUB_CONSULT_MODE;
    },
    lockSelectedModeloLancamento: (state) => {
      state.locked.model.modeloLancamento = state.related.model.modeloLancamento;
    },
    resetLockedModeloLancamentoModel: (state) => {
      state.locked.model.modeloLancamento = {};
    },
    addModeloLancamento: (state, action) => {
      state.status = LOADING_STATUS;

      const newModeloLancamento = action.payload.data;

      const alreadyExists = state.model.modelosLancamento
        .some((existingModeloLancamento) => {
          const relatedDespesaReceitaIdFromExisting = existingModeloLancamento?.despesaReceita?.id;
          const relatedDespesaReceitaIdFromNew = newModeloLancamento?.despesaReceita?.id;

          return relatedDespesaReceitaIdFromExisting === relatedDespesaReceitaIdFromNew;
        });

      if (alreadyExists) {
        const tipoModeloLabelOfNewModeloLancamento = getTipoModeloLabelByValue(
          newModeloLancamento?.tipo
        );

        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.mensagem.modeloAdicionadoTabelaArmazenagem', { tipoModelo: tipoModeloLabelOfNewModeloLancamento, despesa: newModeloLancamento?.despesaReceita?.id }),
        };
        return;
      }

      state.model.modelosLancamento = [
        ...state.model.modelosLancamento,
        {
          ...newModeloLancamento,
          id: uuid(),
        }
      ];
      state.related.model.modeloLancamento = {};
      state.mode.sub = NONE_SUB_MODE;
      state.status = IDLE_STATUS;
    },
    updateModeloLancamento: (state, action) => {
      const updated = action.payload.data;

      const alreadyExists = state.model.modelosLancamento
        .filter((existingModeloLancamento) => {
          const relatedDespesaReceitaIdFromExisting = existingModeloLancamento?.despesaReceita?.id;
          const relatedDespesaReceitaIdFromLocked = (
            state.locked.model.modeloLancamento?.despesaReceita?.id
          );

          return relatedDespesaReceitaIdFromExisting !== relatedDespesaReceitaIdFromLocked;
        })
        .some((existingModeloLancamento) => {
          const relatedDespesaReceitaIdFromExisting = existingModeloLancamento?.despesaReceita?.id;
          const relatedDespesaReceitaIdFromUpdated = updated?.despesaReceita?.id;

          return relatedDespesaReceitaIdFromExisting === relatedDespesaReceitaIdFromUpdated;
        });

      if (alreadyExists) {
        const tipoModeloLabelOfUpdatedModeloLancamento = getTipoModeloLabelByValue(
          updated?.tipo
        );

        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.mensagem.despesaExisteTabelaArmazenagem', { tipoModelo: tipoModeloLabelOfUpdatedModeloLancamento, despesaReceita: updated?.despesaReceita?.id }),
        };
        return;
      }

      state.related.model.modeloLancamento = updated;
      state.model.modelosLancamento = state.model.modelosLancamento.map(
        (current) => ((current?.id === updated?.id)
          ? updated : current
        )
      );
      state.mode.sub = SUB_CONSULT_MODE;
      state.locked.model.modeloLancamento = {};
    },
    removeModeloLancamentoById: (state, action) => {
      const { id } = action.payload.data;
      state.model.modelosLancamento = state.model.modelosLancamento.filter((current) => (
        current?.id !== id
      ));
      state.related.model.modeloLancamento = initialState.related.model.modeloLancamento;
      state.mode.sub = NONE_SUB_MODE;
    },
    resetRelatedModeloLancamentoModel: (state) => {
      state.related.model.modeloLancamento = {};

      if (!isSubCreateMode(state.mode.sub)) {
        state.mode.sub = NONE_SUB_MODE;
      }
    },
    setClientes: (state, action) => {
      state.model.clientes = [...action.payload];
    },
    setResponse: (state, action) => {
      state.response = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearResponse: (state) => {
      state.response = {
        status: null,
        message: null,
        data: null,
      };
    },
    resetModel: (state) => {
      state.model = initialState.model;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllAsync.pending, (state) => {
        state.status = ALTERNATIVE_LOADING_STATUS;
      })
      .addCase(fetchAllAsync.fulfilled, (state, action) => {
        state.status = IDLE_STATUS;
        state.list = action.payload.data;
      })
      .addCase(fetchAllAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaTabelasArmazenagemMensagem', { erro: action.error.message }),
        };
      })
      .addCase(fetchByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(fetchByIdAsync.fulfilled, (state, action) => {
        if (action.payload.response.status === 200) {
          const unnormalizedTabelaPeriodos = action.payload.response.data?.periodos || [];
          const { gris, icms, valorAdicionalCarretaLs } = action.payload.response.data;
          state.model = {
            ...action.payload.response.data,
            gris: `${gris}`,
            icms: `${icms}`,
            valorAdicionalCarretaLs: unnormalizeNumeral(valorAdicionalCarretaLs),
            periodos: unnormalizedTabelaPeriodos.sort((a, b) => {
              const firstPeriodo = parseToNumber(a?.periodo);
              const secondPeriodo = parseToNumber(b?.periodo);
              return firstPeriodo - secondPeriodo;
            }),
          };
          state.status = SUCCESS_STATUS;
        }
      })
      .addCase(fetchByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroCarregarListaTabelasArmazenagemMensagem', { erro: action.error.message }),
        };
      })
      .addCase(activateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(activateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { description } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.tabelaArmazenagemAtivada', { tabela: description });

        if (state.response.status === 200) {
          state.list = state.list
            .map((current) => (
              current.id === action.payload.response.data.id
                ? action.payload.response.data
                : current
            ));
        }
      })
      .addCase(activateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroAtivarTabelaArmazenagem', { erro: action.error.message }),
        };
      })
      .addCase(inactivateByIdAsync.pending, (state) => {
        state.status = LOADING_STATUS;
      })
      .addCase(inactivateByIdAsync.fulfilled, (state, action) => {
        state.status = SUCCESS_STATUS;
        state.response.status = action.payload.response.status;
        state.response.data = action.payload.response.data;

        const { description } = action.payload.response.data;
        state.response.message = i18n.t('com.muralis.qcx.mensagem.tabelaArmazenagemInativada', { tabela: description });

        if (state.response.status === 200) {
          state.list = state.list
            .map((current) => (
              current.id === action.payload.response.data.id
                ? action.payload.response.data
                : current
            ));
        }
      })
      .addCase(inactivateByIdAsync.rejected, (state, action) => {
        state.status = FAILURE_STATUS;
        state.error = {
          ...action.error,
          message: i18n.t('com.muralis.qcx.erro.erroInativarTabelaArmazenagem', { erro: action.error.message }),
        };
      });
  },
});

const {
  changeStatusTo,
  loading,
  success,
  failure,
  resetStatus,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  resetMode,
  changeToBackgroundCreateMode,
  resetBackgroundMode,
  changeToSubCreateMode,
  changeToSubConsultMode,
  changeToSubUpdateMode,
  resetSubMode,
  setModel,
  resetModel,
  addToList,
  updateOnList,
  setRelatedModeloLancamentoModel,
  lockSelectedModeloLancamento,
  resetLockedModeloLancamentoModel,
  addModeloLancamento,
  updateModeloLancamento,
  removeModeloLancamentoById,
  resetRelatedModeloLancamentoModel,
  setRelatedTabelaPeriodoModel,
  addPeriodo,
  updatePeriodo,
  removePeriodoById,
  resetRelatedPeriodoModel,
  setClientes,
  setResponse,
  setResponseMessage,
  clearResponse,
  setError,
  resetList,
} = tabelaArmazenagemSlice.actions;

const selectTabelasArmazenagem = (state) => state.tabelaArmazenagem.list;
const selectStatus = (state) => state.tabelaArmazenagem.status;
const selectMode = (state) => state.tabelaArmazenagem.mode.main;
const selectBackgroundMode = (state) => state.tabelaArmazenagem.mode.background;
const selectSubMode = (state) => state.tabelaArmazenagem.mode.sub;
const selectError = (state) => state.tabelaArmazenagem.error;
const selectResponse = (state) => state.tabelaArmazenagem.response;
const selectTabelaArmazenagem = (state) => state.tabelaArmazenagem.model;
const selectRelatedTabelaPeriodoModel = (state) => (
  state.tabelaArmazenagem.related.model.tabelaPeriodo
);
const selectRelatedModeloLancamentoModel = (state) => (
  state.tabelaArmazenagem.related.model.modeloLancamento
);

export {
  tabelaArmazenagemSlice,
  changeStatusTo,
  loading,
  success,
  failure,
  resetStatus,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  resetMode,
  changeToBackgroundCreateMode,
  resetBackgroundMode,
  changeToSubCreateMode,
  changeToSubConsultMode,
  changeToSubUpdateMode,
  resetSubMode,
  setModel,
  resetModel,
  addToList,
  updateOnList,
  setRelatedModeloLancamentoModel,
  lockSelectedModeloLancamento,
  resetLockedModeloLancamentoModel,
  addModeloLancamento,
  updateModeloLancamento,
  removeModeloLancamentoById,
  resetRelatedModeloLancamentoModel,
  setRelatedTabelaPeriodoModel,
  addPeriodo,
  updatePeriodo,
  removePeriodoById,
  resetRelatedPeriodoModel,
  setClientes,
  setResponse,
  setResponseMessage,
  clearResponse,
  setError,
  resetList,
  fetchAllAsync,
  fetchByIdAsync,
  activateByIdAsync,
  inactivateByIdAsync,
  selectTabelasArmazenagem,
  selectTabelaArmazenagem,
  selectRelatedTabelaPeriodoModel,
  selectRelatedModeloLancamentoModel,
  selectStatus,
  selectMode,
  selectBackgroundMode,
  selectSubMode,
  selectError,
  selectResponse,
};

export default tabelaArmazenagemSlice.reducer;
